import { Button, formatDate, formatMoney, pickerDay } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { candidatesQuery, invoiceActions } from "../../invoices";
import { PAYMENT_STATUS } from "../../labels";
import styles from "./invoices.module.css";
import { type Payer, PayerPicker } from "./payer";

/**
 * An invoice for a payer's unpaid and partly paid orders (P8-47): every one with delivered
 * deliveries not on an invoice yet, all ticked; untick to leave some out. A period narrows the
 * list. A partly paid order is credited what was paid on it. It's made as a draft, which opens
 * for review: issue it, or delete it to start again. The totals are the API's.
 */
export function BatchDialog({
  onClose,
  payer: startPayer = null,
  period: startPeriod = null,
}: {
  onClose: () => void;
  payer?: Payer | null;
  period?: { start: Date; end: Date } | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const format = useCompanyFormat();
  const [payer, setPayer] = useState<Payer | null>(startPayer);
  const [range, setRange] = useState<{ start: Date; end: Date } | null>(startPeriod);
  // the orders left out; everything else is ticked
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const period = range ? { start: pickerDay(range.start), end: pickerDay(range.end) } : null;
  const candidates = useQuery(candidatesQuery(payer?.billee ?? null, period));
  const orders = candidates.data?.orders ?? [];
  const ticked = orders.filter((o) => !unticked.has(o.order_id)).map((o) => o.order_id);
  const some = ticked.length < orders.length;
  // the ticked orders' totals, from the API (no browser arithmetic on money)
  const selection = useQuery({
    ...candidatesQuery(payer?.billee ?? null, period, ticked),
    enabled: Boolean(payer) && some && ticked.length > 0,
    placeholderData: keepPreviousData,
  });
  const totals = some ? (ticked.length ? selection.data?.totals : null) : candidates.data?.totals;
  const money = (v: string) => formatMoney(v, format);
  const mutation = useMutation({
    mutationFn: () =>
      invoiceActions.batch(payer!.billee, { period: period ?? undefined, orderIds: some ? ticked : undefined }),
    onSuccess: async (draft) => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onClose();
      void navigate({ to: "/invoices/$invoiceId", params: { invoiceId: draft.id } });
    },
  });
  const reset = () => setUnticked(new Set());
  const toggle = (id: string, on: boolean) =>
    setUnticked((u) => {
      const next = new Set(u);
      if (on) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Invoice deliveries"
        description="Their unpaid and partly paid orders with deliveries not on an invoice yet, as a draft to review."
      >
        <div className={styles.form}>
          <PayerPicker
            value={payer}
            onChange={(p) => {
              setPayer(p);
              reset();
            }}
          />
          <DateRangePicker
            label="Delivered between (optional)"
            placeholder="Any time"
            value={range}
            onChange={(r) => {
              setRange(r);
              reset();
            }}
          />
          {!payer ? null : candidates.isPending ? (
            <p className={styles.muted}>Finding their orders…</p>
          ) : candidates.error ? (
            <Alert tone="danger" title="Their orders didn't load">
              {messageFor(errorOf(candidates.error))}
            </Alert>
          ) : orders.length === 0 ? (
            <p className={styles.muted}>
              Nothing to invoice: no unpaid or partly paid orders with deliveries left to invoice
              {range ? " in this period" : ""}.
            </p>
          ) : (
            <div className={styles.picker}>
              <Checkbox
                label={`${ticked.length} of ${orders.length} ${orders.length === 1 ? "order" : "orders"}`}
                checked={ticked.length === orders.length ? true : ticked.length === 0 ? false : "indeterminate"}
                onCheckedChange={(on) => setUnticked(on === true ? new Set() : new Set(orders.map((o) => o.order_id)))}
              />
              <ul className={styles.pickList} aria-label="Orders to invoice">
                {orders.map((o) => (
                  <li key={o.order_id}>
                    <Checkbox
                      label={`${o.order_number} · ${money(o.to_invoice)}`}
                      description={[
                        formatDate(o.first_delivered_at, format),
                        o.deliveries === 1 ? "1 delivery" : `${o.deliveries} deliveries`,
                        PAYMENT_STATUS[o.payment_status][0],
                        o.credit !== "0.00" ? `${money(o.credit)} already paid` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      checked={!unticked.has(o.order_id)}
                      onCheckedChange={(on) => toggle(o.order_id, on === true)}
                    />
                  </li>
                ))}
              </ul>
              {totals ? (
                <div className={styles.totals}>
                  <span className={styles.totalRow}>
                    Deliveries <span className={styles.amount}>{money(totals.amount)}</span>
                  </span>
                  {totals.credit !== "0.00" ? (
                    <span className={styles.totalRow}>
                      Already paid <span className={styles.amount}>−{money(totals.credit)}</span>
                    </span>
                  ) : null}
                  <span className={styles.totalRow}>
                    <strong>To invoice</strong> <span className={styles.amount}>{money(totals.to_invoice)}</span>
                  </span>
                </div>
              ) : null}
            </div>
          )}
          {mutation.error ? (
            <Alert tone="danger" title="No invoice made">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!payer || ticked.length === 0}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Make the draft
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
